/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { BEM, useTranslate } from "@adapptio/lib-components";
import { Link, VideoPlayer } from "@adapptio/lib-editor-ui";
import { GettingStarted } from "../../GettingStarted";
import { Card, CardContent, CardIcon, CardText } from "../../../../../ui/components/Card";
import { Icon, ICON_SIZE } from "../../../../../ui/components/Icon";
import { IProductSupport_MenuItem } from "../../../../../productSupport/IProductSupportOpts";
import { useCommandsService } from "../../../../../ui/hooks/useCommandsService";
import { CMD } from "../../../../../commands/CMD";

import "./GettingStartedMain.scss";

export interface IGettingStartedMainProps {
	className?: string;
	view: GettingStarted;
}

export const GettingStartedMain: React.FC<IGettingStartedMainProps> = ({ className, view }) => {
	const baseClass = BEM("edt-getting-started-main");
	const commandsService = useCommandsService();
	const gettingStartedOptions = useMemo(
		() => view.getProductSupportService()?.getGettingStartedOptions(),
		[]
	);
	const socialLinks = useMemo(() => view.getProductSupportService()?.getSocialLinks(), []);
	const [ videoId, setVideoId ] = useState(gettingStartedOptions?.videos[0]?.videoId);
	const t = useTranslate();

	const executeCommand = useCallback((commandId: string, args: unknown[] = []) => {
		commandsService.execute(commandId, ...args).catch((err) => {
			console.error("Failed to execute button command:", commandId, args, err);
		});
	}, []);

	const openMainView = useCallback(() => {
		executeCommand(CMD.VIEW_EDITOR.EDIT.OPEN_MAIN_VIEW);
	}, [ executeCommand ]);

	const createIntegration = useCallback(() => {
		executeCommand("integrationEditor.createPage.open");
	}, [ executeCommand ]);

	const createAction = useCallback(() => {
		executeCommand("actionEditor.create.open");
	}, [ executeCommand ]);

	const getBtnLinkClickHandler = useCallback(
		(item: IProductSupport_MenuItem) => () => {
			executeCommand(item.commandId, item.commandArgs);
		},
		[ executeCommand ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("menu").toClassName()}>
				<div className={baseClass.element("header").toClassName()}>
					<div className={baseClass.element("title").toClassName()}>
						{t("editor", "Getting Started")}
					</div>
					<div className={baseClass.element("subtitle").toClassName()}>
						{t("editor", "Welcome! We're here to help you get things rolling.")}
					</div>
				</div>

				<div className={baseClass.element("menu-group").toClassName()}>
					<Card
						button
						variant="primary"
						onClick={openMainView}
						className={baseClass.element("card").toClassName()}
					>
						<CardContent>
							<CardIcon>
								<Icon name="mdi/monitor-dashboard" size={ICON_SIZE.LARGE} />
							</CardIcon>
							<CardText
								primary={t("editor", "Build User Interface")}
								secondary={t("editor", "Start by editing the main view.")}
							/>
						</CardContent>
					</Card>

					<Card
						button
						variant="primary"
						onClick={createIntegration}
						className={baseClass.element("card").toClassName()}
					>
						<CardContent>
							<CardIcon>
								<Icon name="mdi/database" size={ICON_SIZE.LARGE} />
							</CardIcon>
							<CardText
								primary={t("editor", "Connect Data")}
								secondary={t("editor", "Set up integrations to your data sources.")}
							/>
						</CardContent>
					</Card>

					<Card
						button
						variant="primary"
						onClick={createAction}
						className={baseClass.element("card").toClassName()}
					>
						<CardContent>
							<CardIcon>
								<Icon name="mdi/source-branch" size={ICON_SIZE.LARGE} />
							</CardIcon>
							<CardText
								primary={t("editor", "Create Logic")}
								secondary={t("editor", "Define the bridge between your data and UI.")}
							/>
						</CardContent>
					</Card>
				</div>

				<div className={baseClass.element("menu-group").toClassName()}>
					{gettingStartedOptions.links.map((link) => (
						<Card
							key={link.id}
							button
							className={baseClass.element("menu-btn").toClassName()}
							onClick={getBtnLinkClickHandler(link)}
						>
							<CardContent>
								<CardIcon>
									<Icon name="mdi/file-document" size={ICON_SIZE.LARGE} />
								</CardIcon>
								<CardText
									primary={t("editor", link.label)}
									secondary={t("editor", link.tooltip)}
								/>
							</CardContent>
						</Card>
					))}
				</div>

				<div className={baseClass.element("socials").toClassName()}>
					{socialLinks?.map((sLink) => (
						<Link
							key={sLink.id}
							href={sLink.url}
							title={sLink.title}
							target="_blank"
							className={baseClass.element("socials-link").toClassName()}
						>
							<Icon
								className={baseClass.element("socials-icon").toClassName()}
								name={sLink.icon}
								size={ICON_SIZE.CUSTOM}
							/>
						</Link>
					))}
				</div>
			</div>

			<div className={baseClass.element("content").toClassName()}>
				<div className={baseClass.element("video").toClassName()}>
					<VideoPlayer url={videoId ? `https://youtu.be/${videoId}` : ""} controls={true} />
				</div>

				<div className={baseClass.element("playlist").toClassName()}>
					<div>
						{gettingStartedOptions?.videos?.map((video) => (
							<Card
								key={video.videoId}
								selected={video.videoId === videoId}
								onClick={() => setVideoId(video.videoId)}
								className={baseClass.element("playlist-item").toClassName()}
							>
								<CardContent>
									<CardIcon>
										<Icon name="mdi/play-circle-outline" size={ICON_SIZE.LARGE} />
									</CardIcon>
									<CardText
										primary={t("editor", video.title)}
										secondary={t("editor", video.description)}
									/>
								</CardContent>
							</Card>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};
